import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import Can from '../../../../features/components/Can'
import ListItemsTable from '../../../../features/components/ListItemsTable'
import SocialAuthChecker from '../../../../features/components/SocialAuthChecker'
import ListItemsTotals from '../../../../features/components/ListItemsTotals'
import CampaignGoogleEdit from '../../../../forms/Google/CampaignForms/CampaignGoogleEdit'
import CampaignsChangeHistory from '../../CampaignsChangeHistory'

import useCampaignFetchData from '../../hooks'
import useGetGoogleCampaignActions from './useGetGoogleCampaignActions'

import { createCampaignSummaryPageLink } from '../../helpers'

import {
  campaignsSelector,
  campaignsTotalsSelector,
  campaignsIsLoadingSelector,
  campaignsWasLoadedSelector,
  campaignsErrorSelector,
  campaignDeleteSelector,
  campaignUpdateIsLoadingSelector,
  campaignUpdateSelector,
  campaignsAfterSelector,
  campaignsNextSelector,
  campaignSessionIdSelector
} from '../../../../modules/selectors/campaigns'
import { selectedGoogleAdAccountSelector } from '../../../../modules/selectors/adAccounts'

import { GOOGLE_PLATFORM } from '../../../../constants/selectLists/platformList'
import { ROUTE_PARAMS } from '../../../../constants/routes'

const GoogleCampaignsSection = () => {
  const campaigns = useSelector(campaignsSelector)
  const campaignsIsLoading = useSelector(campaignsIsLoadingSelector)
  const campaignsAfter = useSelector(campaignsAfterSelector)
  const campaignsNext = useSelector(campaignsNextSelector)
  const campaignsSessionId = useSelector(campaignSessionIdSelector)
  const campaignUpdate = useSelector(campaignUpdateSelector)
  const campaignUpdateIsLoading = useSelector(campaignUpdateIsLoadingSelector)
  const campaignDelete = useSelector(campaignDeleteSelector)
  const totalsItem = useSelector(campaignsTotalsSelector)

  const { id: googleAdAccountId, currency_symbol: currencySymbol } = useSelector(selectedGoogleAdAccountSelector)
  const { id: campaignUpdateId } = campaignUpdate
  const { id: campaignDeleteId, isLoading: campaignDeleteIsLoading } = campaignDelete

  const showTotalsItem = !campaignsIsLoading && totalsItem && campaigns.length > 1

  const getGoogleCampaignActions = useGetGoogleCampaignActions()

  const getCampaignsData = useCampaignFetchData({
    adAccountId: googleAdAccountId,
    platform: GOOGLE_PLATFORM,
    withPagination: true
  })

  const loadMoreCampaignsHandler = useCallback(() => {
    getCampaignsData(campaignsAfter, campaignsSessionId, { skipClearingCampaigns: true })
  }, [getCampaignsData, campaignsAfter, campaignsSessionId])

  const summaryPageLinkExtraParams = useMemo(
    () => ({
      [ROUTE_PARAMS.adAccount]: googleAdAccountId
    }),
    [googleAdAccountId]
  )

  return (
    <SocialAuthChecker
      platform={GOOGLE_PLATFORM}
      dataFetch={getCampaignsData}
      adAccountId={googleAdAccountId}
      authFailureText="Please authenticate with Google Ads to retrieve the list of campaigns."
    >
      <ListItemsTable
        items={campaigns}
        platform={GOOGLE_PLATFORM}
        currencySymbol={currencySymbol}
        isLoadingSelector={campaignsIsLoadingSelector}
        errorSelector={campaignsErrorSelector}
        wasLoadedSelector={campaignsWasLoadedSelector}
        createItemLinkHelper={createCampaignSummaryPageLink}
        getActionsDropdownOptions={getGoogleCampaignActions}
        createItemLinkExtraParams={summaryPageLinkExtraParams}
        itemUpdatingId={campaignUpdateIsLoading ? campaignUpdateId : ''}
        itemDeletingId={campaignDeleteIsLoading ? campaignDeleteId : ''}
        loadMoreDataHandler={campaignsNext ? loadMoreCampaignsHandler : null}
      >
        {showTotalsItem && (
          <ListItemsTotals
            items={campaigns}
            data={totalsItem}
            platform={GOOGLE_PLATFORM}
            currencySymbol={currencySymbol}
          />
        )}
      </ListItemsTable>

      <Can I="update" a="Campaign">
        <CampaignGoogleEdit shouldBeCleared />
      </Can>
      <CampaignsChangeHistory platform={GOOGLE_PLATFORM} />
    </SocialAuthChecker>
  )
}

export default GoogleCampaignsSection
